import { useState } from "react";
import ReactMarkdown from "react-markdown";
import { ChevronDown, FileText, Brain, ShieldAlert, Mail, Inbox, Loader2, RefreshCw, Search } from "lucide-react";

const D3 = ["machiavellianism", "psychopathy", "narcissism"];
const B5 = ["extraversion", "agreeableness", "conscientiousness", "neuroticism", "openness"];

const fmtDate = (iso) =>
  iso ? new Date(iso).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" }) : "—";

export const AdminStats = ({ stats }) => {
  if (!stats) return null;
  const cards = [
    { label: "Paid bookings", value: stats.bookings_paid },
    { label: "Revenue", value: `€${(stats.revenue_eur || 0).toLocaleString()}` },
    { label: "Intake forms", value: stats.intake_forms },
    { label: "Quiz leads", value: stats.quiz_leads },
    { label: "Portal clients", value: stats.portal_clients },
    { label: "Premium", value: stats.portal_premium },
    { label: "Incidents logged", value: stats.portal_incidents },
    { label: "Reports built", value: stats.portal_reports },
  ];
  return (
    <div className="grid grid-cols-2 md:grid-cols-4 xl:grid-cols-8 gap-3 mb-8" data-testid="admin-stats">
      {cards.map((c) => (
        <div key={c.label} className="border border-slate-800 bg-slate-900/50 p-4">
          <p className="font-serif text-2xl text-white leading-none">{c.value ?? 0}</p>
          <p className="text-[10px] uppercase tracking-widest text-slate-500 mt-2">{c.label}</p>
        </div>
      ))}
    </div>
  );
};

const Empty = ({ text, testid }) => (
  <div className="border border-dashed border-slate-800 p-12 text-center" data-testid={testid}>
    <Inbox className="text-slate-700 mx-auto mb-4" size={24} />
    <p className="text-slate-500 text-sm">{text}</p>
  </div>
);

const ScoreRow = ({ label, value, max }) => (
  <div className="flex items-center gap-3">
    <span className="text-slate-400 text-xs w-40 shrink-0 capitalize">{label}</span>
    <div className="flex-1 h-[5px] bg-slate-800">
      <div className="h-full bg-[#D4AF37]" style={{ width: `${Math.min(100, (value / max) * 100)}%` }} />
    </div>
    <span className="text-[#D4AF37] font-mono text-xs w-12 text-right">{value}</span>
  </div>
);

export const IntakeFormsTab = ({ forms }) => {
  const [open, setOpen] = useState(null);
  if (!forms.length) return <Empty text="No intake forms submitted yet." testid="admin-no-intake-forms" />;
  return (
    <div className="space-y-3" data-testid="admin-intake-forms">
      {forms.map((form) => {
        const isOpen = open === form.id;
        return (
          <div key={form.id} className="border border-slate-800 bg-slate-900/50">
            <button
              onClick={() => setOpen(isOpen ? null : form.id)}
              data-testid={`intake-form-row-${form.id}`}
              className="w-full flex items-center justify-between gap-4 p-5 text-left hover:bg-slate-900 transition-colors"
            >
              <div className="min-w-0">
                <p className="text-white text-sm truncate">{form.client_name || "Unknown client"}</p>
                <p className="text-slate-500 text-xs mt-1 truncate">
                  {form.client_email} · {form.session_type === "initial" ? "Initial" : "Follow-up"} ·
                  {" "}session {fmtDate(form.session_date)} · submitted {fmtDate(form.created_at)}
                </p>
              </div>
              <div className="flex items-center gap-4 shrink-0">
                <span className="text-[#D4AF37] font-mono text-sm">D3 {form.scores?.leader?.overall ?? "—"}</span>
                <ChevronDown size={16} className={`text-slate-500 transition-transform ${isOpen ? "rotate-180" : ""}`} />
              </div>
            </button>
            {isOpen && (
              <div className="border-t border-slate-800 p-5 md:p-7 grid md:grid-cols-2 gap-8" data-testid={`intake-form-detail-${form.id}`}>
                <div>
                  <p className="text-[10px] uppercase tracking-[0.2em] text-slate-500 mb-4 flex items-center gap-2">
                    <ShieldAlert size={12} className="text-[#D4AF37]" /> Leader dark triad (1–5)
                  </p>
                  <div className="space-y-3 mb-7">
                    {D3.map((t) => (
                      <ScoreRow key={t} label={t} value={form.scores?.leader?.[t] ?? 0} max={5} />
                    ))}
                  </div>
                  <p className="text-[10px] uppercase tracking-[0.2em] text-slate-500 mb-4 flex items-center gap-2">
                    <Brain size={12} className="text-[#D4AF37]" /> Client big five (1–5)
                  </p>
                  <div className="space-y-3">
                    {B5.map((t) => (
                      <ScoreRow key={t} label={t} value={form.scores?.participant?.[t] ?? 0} max={5} />
                    ))}
                  </div>
                </div>
                <div className="space-y-6">
                  <div>
                    <p className="text-[10px] uppercase tracking-[0.2em] text-slate-500 mb-2">Leader behaviour described</p>
                    <p className="text-slate-300 text-sm leading-relaxed whitespace-pre-wrap">{form.leader_behavior || "—"}</p>
                  </div>
                  <div>
                    <p className="text-[10px] uppercase tracking-[0.2em] text-slate-500 mb-2">Coping strategies</p>
                    <p className="text-slate-300 text-sm leading-relaxed whitespace-pre-wrap">{form.coping_strategies || "—"}</p>
                  </div>
                  <div>
                    <p className="text-[10px] uppercase tracking-[0.2em] text-slate-500 mb-2">Details</p>
                    <p className="text-slate-400 text-sm">
                      Leader gender: {form.leader_gender || "—"}
                      {Object.entries(form.demographics || {}).map(([k, v]) => (
                        <span key={k}> · {k}: {v}</span>
                      ))}
                    </p>
                  </div>
                </div>
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
};

export const QuizLeadsTab = ({ results }) => {
  if (!results.length) return <Empty text="No quiz submissions yet." testid="admin-no-quiz-leads" />;
  return (
    <div className="border border-slate-800 bg-slate-900/50 overflow-x-auto" data-testid="admin-quiz-leads">
      <table className="w-full text-sm">
        <thead>
          <tr className="border-b border-slate-800 text-left">
            <th className="p-4 text-[10px] uppercase tracking-[0.2em] text-slate-500 font-normal">Completed</th>
            <th className="p-4 text-[10px] uppercase tracking-[0.2em] text-slate-500 font-normal">Email</th>
            <th className="p-4 text-[10px] uppercase tracking-[0.2em] text-slate-500 font-normal">Risk level</th>
            <th className="p-4 text-[10px] uppercase tracking-[0.2em] text-slate-500 font-normal">Score</th>
            <th className="p-4 text-[10px] uppercase tracking-[0.2em] text-slate-500 font-normal">Answers</th>
          </tr>
        </thead>
        <tbody>
          {results.map((r) => (
            <tr key={r.id} className="border-b border-slate-800/60 last:border-0" data-testid={`quiz-lead-row-${r.id}`}>
              <td className="p-4 text-slate-400">{fmtDate(r.completed_at || r.created_at)}</td>
              <td className="p-4 text-white">
                {r.email ? (
                  <a href={`mailto:${r.email}`} className="hover:text-[#D4AF37] transition-colors">{r.email}</a>
                ) : (
                  <span className="text-slate-600">not captured</span>
                )}
              </td>
              <td className="p-4 text-[#D4AF37]">{r.scores?.level || r.scores?.risk_level || "—"}</td>
              <td className="p-4 text-white font-mono">
                {r.scores?.total ?? r.scores?.score ?? Object.values(r.answers || {}).reduce((a, b) => a + b, 0)}
              </td>
              <td className="p-4 text-slate-500">{Object.keys(r.answers || {}).length} questions</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

export const SessionBriefRow = ({ booking, brief, loading, onGenerate, onEmail }) => (
  <tr className="border-b border-slate-800 bg-slate-900/70" data-testid={`session-brief-row-${booking.id}`}>
    <td colSpan={7} className="p-6">
      {loading && !brief ? (
        <p className="flex items-center gap-3 text-slate-400 text-sm">
          <Loader2 className="w-4 h-4 animate-spin text-[#D4AF37]" />
          Reading the intake form, assessments and incident log…
        </p>
      ) : brief ? (
        <div>
          <div className="flex flex-wrap items-center justify-between gap-4 mb-5">
            <p className="text-[10px] uppercase tracking-[0.2em] text-slate-500">
              Prep brief · generated {new Date(brief.generated_at).toLocaleString("en-GB")}
              {brief.emailed_at && <span className="text-[#D4AF37]"> · emailed to Dr. Gruda</span>}
            </p>
            <div className="flex gap-3">
              <button
                onClick={() => onEmail(booking)}
                disabled={loading}
                data-testid={`brief-email-btn-${booking.id}`}
                className="flex items-center gap-2 border border-slate-700 text-slate-300 px-4 py-2 text-[10px] uppercase tracking-[0.2em] hover:border-[#D4AF37] hover:text-[#D4AF37] disabled:opacity-50 transition-colors"
              >
                <Mail className="w-3 h-3" /> Email to me
              </button>
              <button
                onClick={() => onGenerate(booking)}
                disabled={loading}
                data-testid={`brief-regenerate-btn-${booking.id}`}
                className="flex items-center gap-2 border border-slate-700 text-slate-300 px-4 py-2 text-[10px] uppercase tracking-[0.2em] hover:border-[#D4AF37] hover:text-[#D4AF37] disabled:opacity-50 transition-colors"
              >
                {loading ? <Loader2 className="w-3 h-3 animate-spin" /> : <RefreshCw className="w-3 h-3" />} Regenerate
              </button>
            </div>
          </div>
          <div className="max-w-3xl" data-testid={`brief-content-${booking.id}`}>
            <BriefMarkdown>{brief.brief}</BriefMarkdown>
          </div>
        </div>
      ) : (
        <p className="text-slate-500 text-sm">No brief yet — generate one to prepare for this session.</p>
      )}
    </td>
  </tr>
);

export const BriefMarkdown = ({ children }) => (
  <ReactMarkdown
    components={{
      h2: (p) => <h3 className="text-[#D4AF37] text-[11px] uppercase tracking-[0.2em] mt-6 mb-2 first:mt-0" {...p} />,
      h3: (p) => <h4 className="text-white text-sm font-medium mt-4 mb-2" {...p} />,
      p: (p) => <p className="text-slate-300 text-sm leading-relaxed mb-3" {...p} />,
      strong: (p) => <strong className="text-white font-semibold" {...p} />,
      ul: (p) => <ul className="space-y-2 mb-4" {...p} />,
      ol: (p) => <ol className="space-y-2 mb-4 list-decimal list-inside" {...p} />,
      li: (p) => <li className="text-slate-300 text-sm leading-relaxed marker:text-[#D4AF37]" {...p} />,
      hr: () => <hr className="border-slate-800 my-4" />,
    }}
  >
    {children}
  </ReactMarkdown>
);

export const PortalClientsTab = ({
  clients,
  total = 0,
  page = 0,
  pageSize = 50,
  search = "",
  onSearchChange,
  onPrev,
  onNext,
}) => {
  const [open, setOpen] = useState(null);
  const start = total === 0 ? 0 : page * pageSize + 1;
  const end = Math.min(total, page * pageSize + clients.length);
  const hasPrev = page > 0;
  const hasNext = end < total;
  const pageCount = Math.max(1, Math.ceil(total / pageSize));
  return (
    <div className="space-y-4" data-testid="admin-portal-clients">
      <div className="flex flex-col sm:flex-row sm:items-center gap-3 sm:justify-between">
        <div className="relative w-full sm:max-w-xs">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-600" />
          <input
            type="text"
            value={search}
            onChange={(e) => onSearchChange?.(e.target.value)}
            placeholder="Search by name or email…"
            data-testid="portal-clients-search"
            className="w-full bg-slate-900 border border-slate-800 text-white text-sm pl-9 pr-3 py-2 focus:outline-none focus:border-[#D4AF37] placeholder:text-slate-600"
          />
        </div>
        <p className="text-slate-500 text-xs shrink-0" data-testid="portal-clients-count">
          {total === 0 ? "No accounts" : `Showing ${start}–${end} of ${total.toLocaleString()}`}
        </p>
      </div>

      {clients.length === 0 ? (
        <Empty
          text={search ? "No accounts match your search." : "No portal accounts yet."}
          testid="admin-no-portal-clients"
        />
      ) : (
        <div className="space-y-3">
          {clients.map((c) => {
            const isOpen = open === c.user_id;
            const report = c.latest_report;
            return (
              <div key={c.user_id} className="border border-slate-800 bg-slate-900/50">
                <button
                  onClick={() => setOpen(isOpen ? null : c.user_id)}
                  data-testid={`portal-client-row-${c.user_id}`}
                  className="w-full flex items-center justify-between gap-4 p-5 text-left hover:bg-slate-900 transition-colors"
                >
                  <div className="min-w-0">
                    <p className="text-white text-sm truncate">{c.name}</p>
                    <p className="text-slate-500 text-xs mt-1 truncate">
                      {c.email} · joined {fmtDate(c.created_at)}
                    </p>
                  </div>
                  <div className="flex items-center gap-5 shrink-0">
                    <span className={`text-[10px] uppercase tracking-widest px-2 py-1 border ${
                      c.tier === "free" ? "border-slate-700 text-slate-500" : "border-[#D4AF37] text-[#D4AF37]"
                    }`}>
                      {c.tier}
                    </span>
                    <span className="text-slate-400 text-xs hidden sm:inline">{c.incident_count} incidents</span>
                    <span className="text-slate-400 text-xs hidden md:inline">
                      {c.big_five_completed ? "Big Five ✓" : "Big Five —"}
                    </span>
                    <ChevronDown size={16} className={`text-slate-500 transition-transform ${isOpen ? "rotate-180" : ""}`} />
                  </div>
                </button>
                {isOpen && (
                  <div className="border-t border-slate-800 p-5 md:p-7" data-testid={`portal-client-detail-${c.user_id}`}>
                    <div className="grid md:grid-cols-2 gap-8">
                      <div>
                        <p className="text-[10px] uppercase tracking-[0.2em] text-slate-500 mb-4 flex items-center gap-2">
                          <Brain size={12} className="text-[#D4AF37]" /> Big five (percentile)
                        </p>
                        {c.big_five_scores ? (
                          <div className="space-y-3">
                            {B5.map((t) => <ScoreRow key={t} label={t} value={c.big_five_scores[t] ?? 0} max={100} />)}
                          </div>
                        ) : (
                          <p className="text-slate-500 text-sm">Not completed yet.</p>
                        )}
                        {report?.dark_triad_scores && (
                          <>
                            <p className="text-[10px] uppercase tracking-[0.2em] text-slate-500 mt-7 mb-4 flex items-center gap-2">
                              <ShieldAlert size={12} className="text-[#D4AF37]" /> Their leader (averaged)
                            </p>
                            <div className="space-y-3">
                              {["narcissism", "machiavellianism", "psychopathy"].map((t) => (
                                <ScoreRow key={t} label={t} value={report.dark_triad_scores[t] ?? 0} max={100} />
                              ))}
                            </div>
                          </>
                        )}
                      </div>
                      <div>
                        <p className="text-[10px] uppercase tracking-[0.2em] text-slate-500 mb-4 flex items-center gap-2">
                          <FileText size={12} className="text-[#D4AF37]" /> Latest vulnerability report
                        </p>
                        {report ? (
                          <>
                            <p className="text-slate-500 text-xs mb-4 flex items-center gap-2">
                              {fmtDate(report.created_at)} · {report.incident_count} incidents
                              {report.emailed_at && (
                                <span className="flex items-center gap-1 text-[#D4AF37]">
                                  <Mail size={11} /> emailed
                                </span>
                              )}
                            </p>
                            <ul className="space-y-3 mb-5">
                              {(report.vulnerabilities || []).map((v, i) => (
                                <li key={i} className="border-l-2 border-[#D4AF37]/40 pl-3">
                                  <p className="text-white text-sm">{v.label}</p>
                                  <p className="text-slate-500 text-xs mt-1">
                                    {(v.severity || "").toUpperCase()} · {v.trait_interaction}
                                  </p>
                                </li>
                              ))}
                            </ul>
                            {report.escalation_reason && (
                              <div className="border border-red-500/30 bg-red-500/[0.06] p-4">
                                <p className="text-[10px] uppercase tracking-[0.2em] text-red-400 mb-2">Escalation risk</p>
                                <p className="text-slate-300 text-sm leading-relaxed">{report.escalation_reason}</p>
                              </div>
                            )}
                          </>
                        ) : (
                          <p className="text-slate-500 text-sm">No report generated yet.</p>
                        )}
                      </div>
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {total > pageSize && (
        <div className="flex items-center justify-between pt-2">
          <button
            onClick={onPrev}
            disabled={!hasPrev}
            data-testid="portal-clients-prev"
            className="text-[10px] uppercase tracking-widest px-4 py-2 border border-slate-700 text-slate-300 disabled:opacity-40 disabled:cursor-not-allowed hover:border-[#D4AF37] hover:text-[#D4AF37] transition-colors"
          >
            Previous
          </button>
          <span className="text-slate-500 text-xs">Page {page + 1} of {pageCount}</span>
          <button
            onClick={onNext}
            disabled={!hasNext}
            data-testid="portal-clients-next"
            className="text-[10px] uppercase tracking-widest px-4 py-2 border border-slate-700 text-slate-300 disabled:opacity-40 disabled:cursor-not-allowed hover:border-[#D4AF37] hover:text-[#D4AF37] transition-colors"
          >
            Next
          </button>
        </div>
      )}
    </div>
  );
};
